import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Lightbox } from "@/components/Lightbox";
import { BeforeAfter } from "@/components/BeforeAfter";
import { responsive } from "@/lib/media";

const CATS = ["All", "A/V", "CCTV", "Networking", "Commercial"];
const BASE = process.env.REACT_APP_BACKEND_URL;

const SAMPLE = [
  { id: 1, cat: "A/V", title: "Bel-Air 7.1.4 Atmos theater", subtitle: "In-wall speakers, Kaleidescape source, custom rack.", img: "https://images.unsplash.com/photo-1746439324737-2c9f9a3e81a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxob21lJTIwdGhlYXRlciUyMGx1eHVyeSUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3ODg1NDA2MDJ8MA&ixlib=rb-4.1.0&q=85" },
  { id: 2, cat: "CCTV", title: "Storefront IP camera system", subtitle: "8 PoE cameras, NVR, mobile alerts.", img: "https://images.unsplash.com/photo-1549109926-58f039549485?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGNhbWVyYSUyMGNjdHYlMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzg4NTQwNjAyfDA&ixlib=rb-4.1.0&q=85" },
  { id: 3, cat: "CCTV", title: "Multi-unit residential coverage", subtitle: "Common area + garage cams with retention.", img: "https://images.unsplash.com/photo-1589935447067-5531094415d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHxffHxzZWN1cml0eSUyMGNhbWVyYSUyMGNjdHYlMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzg4NTQwNjAyfDA&ixlib=rb-4.1.0&q=85" },
  { id: 4, cat: "Networking", title: "10G rack + patch panel rebuild", subtitle: "48-port managed switch, VLANs, backhaul.", img: "https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHxffHxzZXJ2ZXIlMjByYWNrJTIwbmV0d29ya2luZyUyMGNhYmxlc3xlbnwwfHx8fDE3ODg1NDA2MDJ8MA&ixlib=rb-4.1.0&q=85" },
  { id: 5, cat: "Networking", title: "Clean patch panel restore", subtitle: "Legacy office wiring re-terminated and labeled.", img: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxzZXJ2ZXIlMjByYWNrJTIwbmV0d29ya2luZyUyMGNhYmxlc3xlbnwwfHx8fDE3ODg1NDA2MDJ8MA&ixlib=rb-4.1.0&q=85" },
  { id: 6, cat: "Commercial", title: "Cafe meeting-room A/V", subtitle: "Ceiling mics, dual displays, Zoom Rooms.", img: "https://images.unsplash.com/photo-1746439324737-2c9f9a3e81a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxob21lJTIwdGhlYXRlciUyMGx1eHVyeSUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3ODg1NDA2MDJ8MA&ixlib=rb-4.1.0&q=85" },
];

export default function Gallery() {
  const [active, setActive] = useState("All");
  const [projects, setProjects] = useState(SAMPLE);
  const [open, setOpen] = useState(null);
  const [params, setParams] = useSearchParams();
  const wanted = params.get("photo");
  useEffect(() => {
    api.get("/gallery").then((r) => {
      if (r.data.length) setProjects(r.data.map((g) => ({ id: g.id, cat: g.category, title: g.title, subtitle: g.subtitle, installed: g.installed !== false, img: `${BASE}${g.img}`, before: g.before_img ? `${BASE}${g.before_img}` : null })));
    }).catch(() => {});
  }, []);
  const items = useMemo(() => active === "All" ? projects : projects.filter(p => p.cat === active), [active, projects]);
  const lightboxItems = useMemo(() => items.map((p) => ({ id: typeof p.id === "string" ? p.id : undefined, src: p.img, before: p.before, title: p.title, subtitle: p.subtitle, tag: p.cat, installed: p.installed })), [items]);
  useEffect(() => {
    if (!wanted) return;
    const i = items.findIndex((p) => String(p.id) === wanted);
    if (i >= 0) setOpen(i);
  }, [wanted, items]);
  const show = (i) => { setOpen(i); setParams(i === null ? {} : { photo: items[i].id }, { replace: true }); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="mb-8 dg-reveal">
        <span className="dg-eyebrow">Project gallery</span>
        <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold tracking-tight font-display">The experience behind the advice.</h1>
        <p className="mt-3 text-slate-300 max-w-2xl">Systems David has built and serviced over the years — and still installs across the LA area. That same hands-on knowledge is what you get on every phone and video session.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => { setActive(c); show(null); }}
            data-testid={`gallery-filter-${c.replace("/", "-").toLowerCase()}`}
            className={`px-4 py-2.5 min-h-[44px] rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
              active === c ? "bg-cyan-500 text-slate-900 border-cyan-500" : "bg-slate-900/50 text-slate-300 border-slate-700 hover:border-cyan-400"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p, i) => (
          <article key={p.id} className={`dg-card rounded-xl overflow-hidden dg-reveal dg-reveal-${(i % 4) + 1}`} data-testid={`gallery-item-${p.id}`}>
            {p.before ? (
              <BeforeAfter before={p.before} after={p.img} alt={p.title} className="h-56" onTap={() => show(i)} />
            ) : (
            <button type="button" onClick={() => show(i)} className="block w-full h-56 overflow-hidden cursor-zoom-in text-left" aria-label={`Open ${p.title}`} data-testid={`gallery-open-${p.id}`}>
              <img {...responsive(p.img)} alt={p.title} loading={i < 2 ? "eager" : "lazy"} decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </button>
            )}
            <div className="p-5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="dg-chip">{p.cat}</span>
                  {p.before && <span className="dg-chip" data-testid={`before-after-tag-${p.id}`}>Before / after</span>}
                </div>
                {p.installed && <span className="dg-chip text-[#FFD600] border-[#FFD600]/50" data-testid={`installed-tag-${p.id}`}>Installed by David</span>}
              </div>
              <h3 className="font-semibold">
                {typeof p.id === "string" ? <Link to={`/project/${p.id}`} className="hover:text-cyan-300 transition-colors" data-testid={`gallery-project-link-${p.id}`}>{p.title}</Link> : p.title}
              </h3>
              <p className="text-sm text-slate-400 mt-1">{p.subtitle}</p>
            </div>
          </article>
        ))}
      </div>
      <Lightbox items={lightboxItems} index={open} onClose={() => show(null)} onChange={show} />
    </div>
  );
}
